import TextInputField from "../common/TextInputField";
import { isDuplicateName } from "../../utils/duplicateName";
import NumericInputField from "../common/NumericInputField";
import React, {
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import PlusIcon from "../../svg/PlusIcon";
import ActionButton from "../../../../common/ui/ActionButton";
import BinSvg from "../../../../common/svgs/BinSvg";
import CustomAutoComplete from "../common/CustomAutoComplete";
import {
  CategoryOption,
  RootStateServiceLogicBuilder,
} from "../../store/service-logic-builder-slice";
import CustomSelect from "../common/CustomSelect";
import CustomDatePicker from "../common/CustomDatePicker";
import {
  getServerCategoryKey,
  getDisplayCategoryName,
  OperatorType,
  Field,
  Rule,
  getOperatorDisplayText,
  getOperatorFromDisplayText,
} from "./NewLogicModal";
import ScrollableWrapper from "../common/ScrollableWrapper";
import { useAuth } from "../../../../providers/AuthProvider";
import HeightInputImperial from "./HeightInputImperial";
import {
  isMeasurementUnitParameter,
  convertWeightToDisplay,
  convertWeightToMetric,
  convertHeightToDisplay,
  convertHeightToMetric,
  getWeightUnit,
  getHeightUnit,
} from "./measurementUnitHelpers";

interface LogicNameSectionProps {
  name: string;
  setLogicName: (str: string) => void;
}

interface RulesSectionProps {
  rules: Rule;
  addCondition: (ruleIndex: number) => void;
  addParameter: (conditionIndex: number) => void;
  deleteCondition: (ruleIndex: number, conditionIndex: number) => void;
  deleteParameter: (conditionIndex: number, fieldIndex: number) => void;
  updateConditionMatch: (match: "all" | "any") => void;
  updateParameterMatch: (conditionIndex: number, match: "all" | "any") => void;
  updateConditionObjectName: (
    conditionIndex: number,
    objectName: string | null
  ) => void;
  updateFieldProperty: (
    conditionIndex: number,
    fieldIndex: number,
    property: keyof Field,
    value: any
  ) => void;
}

interface LogicBuilderStep1Props {
  name: string;
  setLogicName: (str: string) => void;
  rules: Rule;
  addCondition: (ruleIndex: number) => void;
  addParameter: (conditionIndex: number) => void;
  deleteCondition: (ruleIndex: number, conditionIndex: number) => void;
  deleteParameter: (conditionIndex: number, fieldIndex: number) => void;
  updateConditionMatch: (match: "all" | "any") => void;
  updateParameterMatch: (conditionIndex: number, match: "all" | "any") => void;
  updateConditionObjectName: (
    conditionIndex: number,
    objectName: string | null
  ) => void;
  updateFieldProperty: (
    conditionIndex: number,
    fieldIndex: number,
    property: keyof Field,
    value: any
  ) => void;
}

const LogicNameSection = React.memo(
  ({ name, setLogicName }: LogicNameSectionProps) => {
    return (
      <TextInputField
        label="Logic name"
        value={name}
        handleChange={(e) => setLogicName(e.target.value)}
        name="textInput"
      />
    );
  }
);

const RulesSection = React.memo(
  ({
    rules,
    addCondition,
    addParameter,
    deleteCondition,
    deleteParameter,
    updateConditionMatch,
    updateParameterMatch,
    updateConditionObjectName,
    updateFieldProperty,
  }: RulesSectionProps) => {
    const theme: Theme = useTheme();
    const { AuthUser } = useAuth();
    const categoryOptions = useSelector(
      (state: RootStateServiceLogicBuilder) =>
        state.serviceLogicBuilder.categoryOptions
    );
    const scrollableRef = useRef<HTMLDivElement>(null);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 700);

    useEffect(() => {
      const handleResize = () => {
        setIsSmallScreen(window.innerWidth < 700);
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    const scrollToBottom = useCallback(() => {
      if (scrollableRef.current) {
        scrollableRef.current.scrollTo({
          top: scrollableRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, []);

    const questionnaireOptions = useMemo(
      () =>
        Object.keys(categoryOptions).filter(
          (key) =>
            key === "Biometric Questionnaire" ||
            key === "Pnoe Metrics" ||
            key === "Lab Work"
        ),
      [categoryOptions]
    );

    const getFieldOptions = useCallback(
      (categoryType: string | null): CategoryOption[] => {
        if (!categoryType) {
          return [];
        }
        // Convert a server key to display name for lookup
        const displayName = getDisplayCategoryName(categoryType);
        if (!categoryOptions[displayName]) {
          return [];
        }
        return categoryOptions[displayName];
      },
      [categoryOptions]
    );

    const renderChoiceInput = (
      choices: CategoryOption["choices"],
      value: string | number | string[] | number[] | null,
      conditionIndex: number,
      fieldIndex: number
    ) => {
      if (!choices || choices.length === 0) {
        return (
          <div style={styles.fieldPlaceholder(theme)}>No options available</div>
        );
      }

      // Find the current choice by index or id to display the correct label
      const currentChoice = choices.find((choice) => {
        if (choice.index !== undefined) {
          return choice.index.toString() === value?.toString();
        }
        if (choice.id !== undefined) {
          if (Array.isArray(choice.id)) {
            // Choice has array id, value might be comma-separated string
            const valueAsArray =
              typeof value === "string" && value.includes(",")
                ? value
                    .split(",")
                    .map((v) => Number(v.trim()))
                    .sort()
                : [Number(value)];
            return (
              JSON.stringify([...choice.id].sort()) ===
              JSON.stringify(valueAsArray)
            );
          } else {
            return choice.id.toString() === value?.toString();
          }
        }
        return false;
      });

      return (
        <CustomAutoComplete
          label="Value"
          options={choices.map((choice) => choice.label)}
          value={currentChoice?.label || null}
          onChange={(selectedLabel) => {
            // Find the choice by label and save its index or first id
            const selectedChoice = choices.find(
              (choice) => choice.label === selectedLabel
            );
            let valueToSave =
              selectedChoice?.index !== undefined
                ? selectedChoice.index
                : selectedChoice?.id; // Save the entire id (could be number or array)

            // Convert arrays to comma-separated strings for easier handling
            if (Array.isArray(valueToSave)) {
              valueToSave = valueToSave.join(",") as any;
            }
            updateFieldProperty(
              conditionIndex,
              fieldIndex,
              "value",
              valueToSave
            );
          }}
        />
      );
    };

    const renderTimePickerInput = (
      value: string | number | string[] | number[] | null,
      conditionIndex: number,
      fieldIndex: number
    ) => {
      // Generate all time options in 30-minute intervals
      const timeOptions: string[] = [];
      const periods = ["AM", "PM"];

      periods.forEach((period) => {
        for (let hour = 1; hour <= 12; hour++) {
          ["00", "30"].forEach((minute) => {
            timeOptions.push(`${hour}:${minute} ${period}`);
          });
        }
      });

      return (
        <CustomSelect
          label="Value"
          options={timeOptions}
          value={typeof value === "string" ? value : null}
          onChange={(selectedTime) => {
            updateFieldProperty(
              conditionIndex,
              fieldIndex,
              "value",
              selectedTime
            );
          }}
        />
      );
    };

    const renderValueInput = (
      selectedOption: CategoryOption | null,
      _operator: OperatorType | null, // Prefixed with _ to indicate intentionally unused
      value: string | number | string[] | number[] | null,
      conditionIndex: number,
      fieldIndex: number
    ) => {
      if (!selectedOption) {
        return <div style={styles.fieldPlaceholder(theme)}>Value</div>;
      }

      const numericValue = Array.isArray(value) ? null : value;
      const unit = selectedOption.unit;
      const type = selectedOption.type;
      const choices = selectedOption.choices;
      const showMeasurementUnitMessage = isMeasurementUnitParameter(
        selectedOption.id
      );
      // Handle different question types from Biometric Questionnaire
      if (type === "timePicker") {
        // Use custom time picker for time selection
        return renderTimePickerInput(value, conditionIndex, fieldIndex);
      } else if (
        (type === "btn" || type === "list" || type === "selection-list") &&
        choices &&
        choices.length > 0
      ) {
        // Use AutoComplete for button/list/selection-list type questions with choices
        return renderChoiceInput(choices, value, conditionIndex, fieldIndex);
      } else if (type === "scale") {
        // Numeric input for scale questions (weight, height, etc.)
        // Special handling for height in imperial (ft and in)
        if (
          showMeasurementUnitMessage &&
          selectedOption.id === "height" &&
          AuthUser.unitType === 2
        ) {
          // Convert stored cm value to total inches for display
          const displayValueInInches = numericValue
            ? convertHeightToDisplay(Number(numericValue), AuthUser.unitType)
            : null;

          return (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <HeightInputImperial
                value={displayValueInInches}
                onChange={(totalInches) => {
                  // Convert total inches back to cm for storage
                  const valueInCm =
                    totalInches !== null
                      ? convertHeightToMetric(totalInches, AuthUser.unitType)
                      : null;
                  updateFieldProperty(
                    conditionIndex,
                    fieldIndex,
                    "value",
                    valueInCm
                  );
                }}
                name={`value-0-${conditionIndex}-${fieldIndex}`}
              />
              <div style={styles.measurementUnitMessage(theme)}>
                Default unit can be changed in Settings
              </div>
            </div>
          );
        }

        // Handle measurement unit conversion for other parameters (weight, height in metric)
        let displayValue = numericValue;
        let displayUnit = unit;

        if (showMeasurementUnitMessage) {
          // Always show the correct unit based on user's unitType
          if (selectedOption.id === "height") {
            displayUnit = getHeightUnit(AuthUser.unitType);
            if (numericValue !== null) {
              displayValue = convertHeightToDisplay(
                Number(numericValue),
                AuthUser.unitType
              );
            }
          } else if (
            selectedOption.id === "current_weight" ||
            selectedOption.id === "target_weight"
          ) {
            displayUnit = getWeightUnit(AuthUser.unitType);
            if (numericValue !== null) {
              displayValue = convertWeightToDisplay(
                Number(numericValue),
                AuthUser.unitType
              );
            }
          }
        }

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <NumericInputField
              label="Value"
              unit={displayUnit}
              value={displayValue?.toString() || ""}
              handleChange={(e) => {
                let valueToSave = e.target.value ? Number(e.target.value) : null;

                // Convert from display unit back to metric for storage
                if (
                  showMeasurementUnitMessage &&
                  valueToSave !== null &&
                  selectedOption
                ) {
                  if (selectedOption.id === "height") {
                    valueToSave = convertHeightToMetric(
                      valueToSave,
                      AuthUser.unitType
                    );
                  } else if (
                    selectedOption.id === "current_weight" ||
                    selectedOption.id === "target_weight"
                  ) {
                    valueToSave = convertWeightToMetric(
                      valueToSave,
                      AuthUser.unitType
                    );
                  }
                }

                updateFieldProperty(
                  conditionIndex,
                  fieldIndex,
                  "value",
                  valueToSave
                );
              }}
              name={`value-0-${conditionIndex}-${fieldIndex}`}
            />
            {showMeasurementUnitMessage && (
              <div style={styles.measurementUnitMessage(theme)}>
                Default unit can be changed in Settings
              </div>
            )}
          </div>
        );
      } else if (type === "datepicker") {
        // Date input for birthdate using CustomDatePicker
        const currentDate = numericValue
          ? new Date(numericValue.toString())
          : null;
        return (
          <CustomDatePicker
            label="Value"
            value={currentDate}
            onChange={(date) => {
              if (date) {
                // Format date as YYYY-MM-DD using local time (not UTC)
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const formattedDate = `${year}-${month}-${day}`;
                updateFieldProperty(
                  conditionIndex,
                  fieldIndex,
                  "value",
                  formattedDate
                );
              }
            }}
            placeholder="Select date"
          />
        );
      }

      // Handle legacy Pnoe Metrics units
      if (unit === "%") {
        return (
          <CustomSelect
            label="Value"
            options={["0%", "20%", "40%", "60%", "80%", "100%"]}
            value={numericValue?.toString() ? `${numericValue}%` : null}
            onChange={(value) => {
              // Remove the % symbol before saving
              const numericValue = value ? value.replace("%", "") : null;
              updateFieldProperty(
                conditionIndex,
                fieldIndex,
                "value",
                numericValue ? Number(numericValue) : null
              );
            }}
          />
        );
      } else if (unit === "high/low") {
        return (
          <CustomSelect
            label="Value"
            options={["High", "Low"]}
            value={numericValue?.toString() || null}
            onChange={(value) => {
              updateFieldProperty(conditionIndex, fieldIndex, "value", value);
            }}
          />
        );
      } else {
        // Default to numeric input for other units (ml/min/kg, etc.)
        return (
          <NumericInputField
            label="Value"
            unit={unit}
            value={numericValue?.toString() || ""}
            handleChange={(e) => {
              updateFieldProperty(
                conditionIndex,
                fieldIndex,
                "value",
                e.target.value ? Number(e.target.value) : null
              );
            }}
            name={`value-0-${conditionIndex}-${fieldIndex}`}
          />
        );
      }
    };

    return (
      <div style={styles.rulesContainer}>
        <div style={styles.rulesWithScrollContainer}>
          <div style={styles.ruleSetupDetailContainer}>
            <div style={styles.ruleSetupHeaderTitle(theme)}>Rule Setup</div>
            <div style={styles.ruleSetupHeaderText(theme)}>
              Build a rule by adding conditions. Combine them with ‘And’ (all
              must apply) or ‘Or’ (any one applies).
            </div>
          </div>
          <ScrollableWrapper ref={scrollableRef}>
            <div style={styles.scrollableContent}>
              <div style={styles.conditionTemplate(theme)}>
                <div style={styles.conditionTitleLeft}>
                  <div style={styles.conditionTitle(theme)}>Condition(s)</div>
                  {rules.conditions.length > 1 && (
                    <div style={{ width: "100px" }}>
                      <CustomSelect
                        label=""
                        options={["All (and)", "Any (or)"]}
                        value={rules.match === "all" ? "All (and)" : "Any (or)"}
                        onChange={(value) => {
                          const matchValue =
                            value === "All (and)" ? "all" : "any";
                          updateConditionMatch(matchValue as "all" | "any");
                        }}
                        customStyles={{ height: "33px", padding: "4px 8px" }}
                      />
                    </div>
                  )}
                </div>

                {/* Render all conditions in this rule */}
                {rules.conditions.map((condition, conditionIndex) => {
                  const fieldOptions = getFieldOptions(
                    condition.object_name || null
                  );
                  const defaultOperatorOptions = Object.values(
                    OperatorType
                  ).slice(0, -1);

                  return (
                    <div key={`condition-${conditionIndex}`}>
                      {conditionIndex > 0 && (
                        <div style={styles.conditionSeparator(theme)}>
                          <div style={styles.conditionSeparatorText(theme)}>
                            {rules.match === "all" ? "All" : "Any"}
                          </div>
                        </div>
                      )}
                      <div style={styles.conditionFields(theme)}>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <CustomAutoComplete
                            label="Category"
                            options={questionnaireOptions}
                            value={
                              condition.object_name
                                ? getDisplayCategoryName(condition.object_name)
                                : null
                            }
                            onChange={(value) => {
                              const serverKey = value
                                ? getServerCategoryKey(value)
                                : null;
                              updateConditionObjectName(
                                conditionIndex,
                                serverKey
                              );
                              // Reset the field option when category changes
                              updateFieldProperty(
                                conditionIndex,
                                0,
                                "name",
                                ""
                              );
                              updateFieldProperty(
                                conditionIndex,
                                0,
                                "operator",
                                null
                              );
                              updateFieldProperty(
                                conditionIndex,
                                0,
                                "value",
                                null
                              );
                            }}
                          />
                          <div
                            onClick={() => {
                              if (rules.conditions.length === 1) return;
                              deleteCondition(0, conditionIndex);
                            }}
                            style={{
                              cursor:
                                rules.conditions.length === 1
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            <BinSvg
                              color={
                                rules.conditions.length === 1
                                  ? theme.colorTokens.N[60]
                                  : undefined
                              }
                            />
                          </div>
                        </div>
                        <div style={styles.parameterTitleLeft}>
                          <div style={styles.parameterTitle(theme)}>
                            Parameter(s)
                          </div>
                          {condition.fields.length > 1 && (
                            <div style={{ width: "100px" }}>
                              <CustomSelect
                                label=""
                                options={["All (and)", "Any (or)"]}
                                value={
                                  condition.match === "all"
                                    ? "All (and)"
                                    : "Any (or)"
                                }
                                onChange={(value) => {
                                  const matchValue =
                                    value === "All (and)" ? "all" : "any";
                                  updateParameterMatch(
                                    conditionIndex,
                                    matchValue as "all" | "any"
                                  );
                                }}
                                customStyles={{
                                  height: "33px",
                                  padding: "4px 8px",
                                }}
                              />
                            </div>
                          )}
                        </div>

                        {/* Render all parameters/fields in this condition */}
                        {condition.fields.map((field, fieldIndex) => (
                          <div
                            key={`field-${fieldIndex}`}
                            data-field-id={`field-${conditionIndex}-${fieldIndex}`}
                          >
                            {fieldIndex > 0 && (
                              <div style={styles.parameterSeparator(theme)}>
                                <div
                                  style={styles.parameterSeparatorText(theme)}
                                >
                                  {condition.match === "all" ? "All" : "Any"}
                                </div>
                              </div>
                            )}
                            <div style={styles.parameterFields(isSmallScreen)}>
                              <CustomAutoComplete
                                label="Parameter"
                                options={fieldOptions.map(
                                  (option) => option.label
                                )}
                                value={
                                  fieldOptions.find(
                                    (option) => option.id === field?.name
                                  )?.label || null
                                }
                                onChange={(value) => {
                                  const selectedOption = fieldOptions.find(
                                    (option) => option.label === value
                                  );
                                  updateFieldProperty(
                                    conditionIndex,
                                    fieldIndex,
                                    "name",
                                    selectedOption ? selectedOption.id : ""
                                  );
                                  // Reset operator and value when field changes
                                  updateFieldProperty(
                                    conditionIndex,
                                    fieldIndex,
                                    "operator",
                                    null
                                  );
                                  updateFieldProperty(
                                    conditionIndex,
                                    fieldIndex,
                                    "value",
                                    null
                                  );
                                }}
                              />
                              <div
                                style={{
                                  flex: 0.75,
                                }}
                              >
                                <CustomAutoComplete
                                  label="Operator"
                                  placeholder={"Select"}
                                  options={
                                    field?.name
                                      ? ((): string[] => {
                                          const selectedFieldOption =
                                            fieldOptions.find(
                                              (option) =>
                                                option.id === field?.name
                                            );
                                          const operators =
                                            selectedFieldOption?.availableOperators ||
                                            defaultOperatorOptions;
                                          // Map operators to display text
                                          return operators.map((op) =>
                                            getOperatorDisplayText(
                                              op as OperatorType
                                            )
                                          );
                                        })()
                                      : []
                                  }
                                  value={
                                    field?.operator
                                      ? getOperatorDisplayText(field.operator)
                                      : null
                                  }
                                  onChange={(displayText) => {
                                    // Convert display text back to operator symbol
                                    const operatorValue =
                                      getOperatorFromDisplayText(
                                        displayText || ""
                                      );
                                    updateFieldProperty(
                                      conditionIndex,
                                      fieldIndex,
                                      "operator",
                                      operatorValue
                                    );
                                  }}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                {renderValueInput(
                                  fieldOptions.find(
                                    (option) => option.id === field?.name
                                  ) || null,
                                  field?.operator || null,
                                  field?.value ?? null,
                                  conditionIndex,
                                  fieldIndex
                                )}
                              </div>
                              <div
                                onClick={() => {
                                  if (condition.fields.length === 1) return;
                                  deleteParameter(conditionIndex, fieldIndex);
                                }}
                                style={{
                                  paddingTop: isSmallScreen ? "" : "18px",
                                  cursor:
                                    condition.fields.length === 1
                                      ? "not-allowed"
                                      : "pointer",
                                  opacity:
                                    condition.fields.length === 1 ? 0.5 : 1,
                                }}
                              >
                                <BinSvg
                                  color={
                                    condition.fields.length === 1
                                      ? theme.colorTokens.N[60]
                                      : undefined
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <div style={{ width: "180px", height: "16px" }} />
                        <ActionButton
                          icon={
                            <PlusIcon
                              fill={theme.colors_ds.PRIMARY_ICON_MAIN}
                            />
                          }
                          label={"Add parameter"}
                          onClick={() => {
                            addParameter(conditionIndex);
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div style={{ width: "180px", height: "16px" }} />
                <ActionButton
                  icon={<PlusIcon fill={theme.colors_ds.PRIMARY_ICON_MAIN} />}
                  label={"Add condition"}
                  onClick={() => {
                    addCondition(0);
                    setTimeout(scrollToBottom, 100);
                  }}
                />
              </div>
            </div>
          </ScrollableWrapper>
        </div>
      </div>
    );
  }
);

const LogicBuilderStep1 = ({
  name,
  setLogicName,
  rules,
  addCondition,
  addParameter,
  deleteCondition,
  deleteParameter,
  updateConditionMatch,
  updateParameterMatch,
  updateConditionObjectName,
  updateFieldProperty,
}: LogicBuilderStep1Props) => {
  return (
    <div style={styles.mainContainer}>
      <LogicNameSection name={name} setLogicName={setLogicName} />
      <RulesSection
        rules={rules}
        addCondition={addCondition}
        addParameter={addParameter}
        deleteCondition={deleteCondition}
        deleteParameter={deleteParameter}
        updateConditionMatch={updateConditionMatch}
        updateParameterMatch={updateParameterMatch}
        updateConditionObjectName={updateConditionObjectName}
        updateFieldProperty={updateFieldProperty}
      />
    </div>
  );
};

export default LogicBuilderStep1;

// Validation function for step 1
export const validateStep1 = (
  name: string,
  rules: Rule,
  categoryOptions: Record<string, CategoryOption[]>,
  existingLogics: { id?: string | null; name?: string | null }[] = [],
  excludeId: string | null = null
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // 1. Validate logic name
  if (!name || name.trim() === "") {
    errors.push("Please enter a logic name");
  } else if (isDuplicateName(name, existingLogics, excludeId)) {
    errors.push("A logic with this name already exists");
  }

  // Helper to get field label from id
  const getFieldLabel = (categoryKey: string, fieldId: string): string => {
    const displayName = getDisplayCategoryName(categoryKey);
    const options = categoryOptions[displayName];
    if (!options) return fieldId;

    const option = options.find((opt) => opt.id === fieldId);
    return option ? option.label : fieldId;
  };

  // 2. Validate each condition
  rules.conditions.forEach((condition, conditionIndex) => {
    const conditionNumber = conditionIndex + 1;

    // Check if object_name (category) is set
    if (!condition.object_name) {
      errors.push(`Condition ${conditionNumber} has missing values`);
      return; // Skip further validation for this condition
    }

    // Get the display name for better error messages
    const categoryDisplayName = getDisplayCategoryName(condition.object_name);

    // Check if all fields are properly filled
    const hasIncompleteFields = condition.fields.some((field) => {
      // Check if name is empty or null
      const isNameEmpty = !field.name || field.name === "";
      // Check if operator is null
      const isOperatorNull = field.operator === null;
      // Check if value is null or empty string
      const isValueNull = field.value === null || field.value === "";

      return isNameEmpty || isOperatorNull || isValueNull;
    });

    if (hasIncompleteFields) {
      // Find which parameter has missing values (for better error message)
      const incompleteField = condition.fields.find((field) => {
        const isNameEmpty = !field.name || field.name === "";
        const isOperatorNull = field.operator === null;
        const isValueNull = field.value === null || field.value === "";
        return isNameEmpty || isOperatorNull || isValueNull;
      });

      // If we have a parameter name, include it in the error message
      if (incompleteField && incompleteField.name) {
        const parameterLabel = getFieldLabel(
          condition.object_name,
          incompleteField.name
        );
        errors.push(
          `Condition for category "${categoryDisplayName}" and parameter "${parameterLabel}" has missing values`
        );
      } else {
        // If no parameter is selected yet, just mention the category
        errors.push(
          `Condition for category "${categoryDisplayName}" has missing values`
        );
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const styles = {
  mainContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
  },
  rulesContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
    marginTop: "32px",
  },
  rulesWithScrollContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
  },
  scrollableContent: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    paddingBottom: "20px",
    border: ` 1px solid var(--border-color-light)`,
  },
  ruleSetupDetailContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    marginBottom: "16px",
  },
  ruleSetupHeaderTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  ruleSetupHeaderText: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "-0.24px",
  }),
  conditionTemplate: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
  }),
  conditionTitleLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "16px",
  },
  conditionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  conditionFields: (theme: Theme) => ({
    padding: `16px`,
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colorTokens.N[10],
    boxShadow: "var(--box-shadow-general)",
  }),
  parameterTitleLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "16px",
    marginBottom: "16px",
  },
  parameterFields: (isSmallScreen: boolean) => ({
    display: "flex",
    flexDirection: isSmallScreen ? ("column" as "column") : ("row" as "row"),
    gap: "16px",
    marginTop: "8px",
    alignItems: isSmallScreen ? "stretch" : "flex-start",
  }),
  parameterTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  parameterSeparator: (theme: Theme) => ({
    width: "100%",
    borderBottom: `1px solid ${theme.colorTokens.N[60]}`,
    margin: "24px 0",
    position: "relative" as "relative",
  }),
  parameterSeparatorText: (theme: Theme) => ({
    position: "absolute" as "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "60px",
    textAlign: "center" as "center",
    backgroundColor: theme.colorTokens.N[10],
    fontSize: "14px",
    fontFamily: "var(--font-Bold)",
    color: theme.colorTokens.N[60],
  }),
  conditionSeparator: (theme: Theme) => ({
    width: "100%",
    borderBottom: `1px solid ${theme.colorTokens.N[70]}`,
    margin: "24px 0",
    position: "relative" as "relative",
  }),
  conditionSeparatorText: (theme: Theme) => ({
    position: "absolute" as "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "60px",
    textAlign: "center" as "center",
    backgroundColor: theme.colorTokens.N[10],
    fontSize: "14px",
    fontFamily: "var(--font-Bold)",
    color: theme.colorTokens.N[70],
  }),
  fieldPlaceholder: (theme: Theme) => ({
    flex: 1,
    height: "56px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colorTokens.N[30],
    display: "flex",
    alignItems: "center",
    padding: "12px",
    justifyContent: "flex-start",
    position: "relative" as "relative",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
  }),
  measurementUnitMessage: (theme: Theme) => ({
    fontSize: "12px",
    fontFamily: "var(--font-Medium)",
    color: theme.colorTokens.N[80],
    lineHeight: "14px",
  }),
};
